import {
  Button,
  CollapseLayer,
  CollapseLayerGrid,
  CollapseLayerProvider,
  CollapseTrigger,
  GridColumn,
  Separator,
  Spinner,
  TableCell,
  TableCollapse,
  TableCollapseTrigger,
  Text
} from 'telia-front-react';
import { inject, observer } from 'mobx-react';
import * as React from 'react';

import AnalyticsService from '../../../services/AnalyticsService';
import InvoiceDetailsStore from '../../../stores/InvoiceDetailsStore';
import { Transaction } from '../../../stores/models/Transaction';
import { IPaymentDetailsStore } from '../../../stores/PaymentDetailsStore';
import { IPaymentHistoryStore } from '../../../stores/PaymentHistoryStore';
import { ITranslateStore } from '../../../stores/TranslateStore';
import { formatAsCurrency } from '../../../util/FormatUtil';
import WarningNotice from '../notices/WarningNotice';

import InvoiceNameStore from '../../../stores/InvoiceNameStore';
import { formatDate } from '../../../util/DateUtil';
import DownloadXlsxButton from './DownloadXlsxButton';
import HirePurchaseAgreementDetails from './HirePurchaseAgreementDetails';
import { paymentDetailsRow } from './PaymentDetails';
import PeriodAmountsGrid from './PeriodAmountsGrid';
import TransactionFilter from './TransactionFilter';
import ViewInvoiceDetailsButton from './ViewInvoiceDetailsButton';
import CorrectionTransactionDetails from './CorrectionTransactionDetails';

interface IPaymentHistoryMobileProps {
  paymentHistoryStore?: IPaymentHistoryStore;
  invoiceDetailsStore?: InvoiceDetailsStore;
  translateStore?: ITranslateStore;
  paymentDetailsStore?: IPaymentDetailsStore;
  invoiceNameStore?: InvoiceNameStore;
  analyticsService?: AnalyticsService;
}

@inject('paymentHistoryStore', 'translateStore', 'invoiceDetailsStore', 'paymentDetailsStore', 'invoiceNameStore', 'analyticsService')
@observer
export default class PaymentHistoryMobile extends React.Component<IPaymentHistoryMobileProps> {
  public render(): JSX.Element {
    const { openingDate, closingDate } = this.props.paymentHistoryStore;
    const translate = this.props.translateStore.translate;

    return (
      <Text>
        {this.failedToDownloadFileNotice()}
        <h1>{translate('payment-history.title')}</h1>
        {translate('payment-history.transactions.title')}
        <h4>{`${formatDate(openingDate)} - ${formatDate(closingDate)}`}</h4>
        <CollapseLayerGrid id="previous-invoices-collapse" align={['start-xs', 'top-xs']}>
          <GridColumn width={['xs']}>
            <CollapseLayerProvider id="collapse-filter">
              <CollapseTrigger>
                <Button
                  variant="secondary"
                  text={translate('payment-history.filter.button')}
                  size="small"
                  iconLeft="#menu-burger"
                  onClick={(isVisible) => {
                    if (isVisible) {
                      this.props.analyticsService.paymentHistoryOpenFilter();
                    }
                  }}
                />
              </CollapseTrigger>
              <CollapseLayer>
                <TransactionFilter />
              </CollapseLayer>
            </CollapseLayerProvider>
          </GridColumn>
          <GridColumn width={['xs']}>
            <DownloadXlsxButton />
          </GridColumn>
        </CollapseLayerGrid>

        {this.transactionsHeaderAndTableContent()}
      </Text>
    );
  }

  private transactionsHeaderAndTableContent() {
    const paymentHistoryStore = this.props.paymentHistoryStore;
    const amountsFontStyleMobile = 'h4';
    const store = this.props.paymentHistoryStore;

    if (store.transactions.isFinishedWithError) {
      return <WarningNotice translationKey="payment-history.errors.loading-transactions-failed" />;
    }

    if (store.transactions.isInitialLoading) {
      return <Spinner />;
    }

    return (
      <>
        <PeriodAmountsGrid amountsFontStyle={amountsFontStyleMobile} />
        <TableCollapse
          onToggle={(openItems) => {
            for (const openItem of openItems) {
              if (openItem.startsWith('bindingId-')) {
                const bindingId: number = parseInt(openItem.substr('bindingId-'.length), 10);
                this.props.paymentDetailsStore.tryLoadPaymentDetails(bindingId);
              }
            }
            this.props.paymentDetailsStore.setOpenLines(openItems);
            this.props.analyticsService.paymentHistoryViewTransactionsDetails();
          }}
          openItems={this.props.paymentDetailsStore.getOpenLines()}
        >
          <tbody>{paymentHistoryStore.transactions.payload.map((transaction, i) => this.mapToTransactionRows(transaction, i))}</tbody>
        </TableCollapse>
      </>
    );
  }

  private mapToTransactionRows(transaction: Transaction, index: number): JSX.Element[] {
    const translate = this.props.translateStore.translate;

    if (transaction.type === Transaction.TYPE_INVOICE) {
      return this.invoice(transaction, index);
    }

    if (transaction.type === Transaction.TYPE_RECEIPT) {
      return this.payment(transaction, index);
    }

    if (transaction.type === Transaction.TYPE_HIRE_PURCHASE_AGREEMENT) {
      return this.hirePurchaseAgreement(transaction, index);
    }

    if (transaction.type === Transaction.TYPE_CORRECTION_TRANSACTION) {
      return this.correctionTransaction(transaction, index);
    }

    return [
      <tr key={index} id={`payment-history-row-${index}`}>
        <TableCell width={4} valign="top">
          <div className="text-bold">{translate(`shared.transactions.names.${transaction.type}`)}</div>
          <div>{formatDate(transaction.date)}</div>
        </TableCell>
        <TableCell width={4} valign="top">
          <div className="text-right">{translate('payment-history.transactions.amount')}</div>
          <div className="text-right c-brand-primary-text text-bold">{formatAsCurrency(transaction.amount)}</div>
        </TableCell>
        <TableCell width={3} valign="top">
          <div className="text-right">{translate('payment-history.transactions.balance')}</div>
          <div className="text-right">{formatAsCurrency(transaction.balance)}</div>
        </TableCell>
        <TableCell width={1} />
      </tr>
    ];
  }

  private invoice(transaction: Transaction, index: number): JSX.Element[] {
    const dataRowTrigger = `invoice-${index}`;
    const referenceNumberName = this.props.invoiceNameStore.refNumberName(
      transaction.invoiceContentType,
      transaction.invoiceReferenceNumberAlias,
      transaction.invoiceSource
    );
    const translate = this.props.translateStore.translate;

    return [
      <tr key={index} id={`payment-history-invoice-row-${index}`} data-row-trigger={`#${dataRowTrigger}`}>
        <TableCell width={5} valign="top">
          <TableCollapseTrigger url={`#${dataRowTrigger}`} text={translate('shared.transactions.names.invoice')} link={true} />
          <div>{formatDate(transaction.date)}</div>
          <div className="text-bold">{referenceNumberName}</div>
        </TableCell>
        <TableCell width={3} valign="top">
          <div className="text-right">{translate('payment-history.transactions.amount')}</div>
          <div className="text-right c-brand-primary-text text-bold">{formatAsCurrency(transaction.amount)}</div>
        </TableCell>
        <TableCell width={3} valign="top">
          <div className="text-right">{translate('payment-history.transactions.balance')}</div>
          <div className="text-right">{formatAsCurrency(transaction.balance)}</div>
        </TableCell>
        <TableCell width={1} className="text-right" valign="middle">
          <TableCollapseTrigger url={`#${dataRowTrigger}`} text="invoice" link={true} icon="#arrow-down" />
        </TableCell>
      </tr>,
      <tr key={`payment-details-${index}`} id={dataRowTrigger} data-table-collapse={true} style={{ borderTop: 'none' }}>
        <TableCell width={12} colSpan={4}>
          <div>{translate('payment-history.transactions.reference-number')}</div>
          <div>{transaction.invoiceReferenceNumber}</div>
          <Separator spacing="xxsmall" color="invisible" />
          <ViewInvoiceDetailsButton index={index} transaction={transaction} />
        </TableCell>
      </tr>
    ];
  }

  private payment(transaction: Transaction, index: number): JSX.Element[] {
    const dataRowTrigger: string = transaction.receiptBindingId ? `bindingId-${transaction.receiptBindingId}` : `payment-without-bindingId-${index}`;

    return this.collapsibleReceipt(index, dataRowTrigger, transaction, paymentDetailsRow(transaction));
  }

  private hirePurchaseAgreement(transaction: Transaction, index: number): JSX.Element[] {
    const dataRowTrigger = `hire-purchase-agreement-${index}`;

    return this.collapsibleReceipt(index, dataRowTrigger, transaction, <HirePurchaseAgreementDetails transaction={transaction} />);
  }

  private correctionTransaction(transaction: Transaction, index: number): JSX.Element[] {
    const dataRowTrigger = `prepayment-refunded-${index}`;

    return this.collapsibleReceipt(index, dataRowTrigger, transaction, <CorrectionTransactionDetails transaction={transaction} />);
  }

  private collapsibleReceipt(index: number, dataRowTrigger: string, transaction: Transaction, detailsRow: JSX.Element) {
    const translate = this.props.translateStore.translate;

    return [
      <React.Fragment key={index}>
        <tr id={`payment-history-${transaction.type}-row-${index}`} data-row-trigger={`#${dataRowTrigger}`}>
          <TableCell width={5} valign="top">
            <TableCollapseTrigger url={`#${dataRowTrigger}`} text={translate(`shared.transactions.names.${transaction.type}`)} link={true} />
            <div>{formatDate(transaction.date)}</div>
          </TableCell>
          <TableCell width={3} valign="top">
            <div className="text-right">{translate('payment-history.transactions.amount')}</div>
            <div className="text-right c-brand-primary-text text-bold">{formatAsCurrency(transaction.amount)}</div>
          </TableCell>
          <TableCell width={3} valign="top">
            <div className="text-right">{translate('payment-history.transactions.balance')}</div>
            <div className="text-right">{formatAsCurrency(transaction.balance)}</div>
          </TableCell>
          <TableCell width={1} className="text-right" valign="middle">
            <TableCollapseTrigger url={`#${dataRowTrigger}`} text="receipt" link={true} icon="#arrow-down" />
          </TableCell>
        </tr>
        <tr key={`receipt-details-${index}`} id={dataRowTrigger} data-table-collapse={true} style={{ borderTop: 'none' }}>
          <TableCell width={12} colSpan={4}>
            <Separator spacing="xsmall" color="invisible" />
            {this.props.paymentDetailsStore.isLineOpened(dataRowTrigger) ? detailsRow : <React.Fragment />}
            <Separator spacing="xsmall" color="invisible" />
          </TableCell>
        </tr>
      </React.Fragment>
    ];
  }

  private failedToDownloadFileNotice() {
    return this.props.paymentHistoryStore.fileStatus === 'error' ? (
      <WarningNotice translationKey="file.payment-history.transactions.errors.file-loading-failed" />
    ) : (
      <React.Fragment />
    );
  }
}
